import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DomainConfigComponent } from '../domain-config.component';

export interface MicrosoftTokens {
  accessToken: string;
  refreshToken: string;
  expiresOn: Date;
  scopes: string[];
}

export interface MicrosoftUser {
  id: string;
  displayName: string;
  mail: string;
  userPrincipalName: string;
}

export interface MicrosoftCalendarEvent {
  id: string;
  subject: string;
  body?: {
    content: string;
    contentType: string;
  };
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
  location?: {
    displayName: string;
  };
  attendees?: Array<{
    emailAddress: {
      address: string;
      name: string;
    };
    status: {
      response: string;
    };
  }>;
  organizer?: {
    emailAddress: {
      address: string;
      name: string;
    };
  };
  isCancelled: boolean;
  webLink?: string;
  createdDateTime?: string;
  lastModifiedDateTime?: string;
}

@Injectable({
  providedIn: 'root'
})
export class MicrosoftCalendarService {
  private readonly STORAGE_KEY = 'microsoft_calendar_tokens';

  private tokensSubject = new BehaviorSubject<MicrosoftTokens | null>(null);
  private userSubject = new BehaviorSubject<MicrosoftUser | null>(null);

  public tokens$ = this.tokensSubject.asObservable();
  public user$ = this.userSubject.asObservable();

  constructor(
    private http: HttpClient,
    private domainConfig: DomainConfigComponent
  ) {
    this.loadStoredTokens();
  }

  /**
   * Obtener la URL del API de calendario desde la configuración del dominio
   */
  private get API_BASE_URL(): string {
    return this.domainConfig.configCalendarApiUrl;
  }

  private loadStoredTokens(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const tokens = JSON.parse(stored);
        tokens.expiresOn = new Date(tokens.expiresOn);
        this.tokensSubject.next(tokens);
      }
    } catch (error) {
      console.error('Error loading stored Microsoft tokens:', error);
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  private saveTokens(tokens: MicrosoftTokens): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(tokens));
    this.tokensSubject.next(tokens);
  }

  private clearTokens(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.tokensSubject.next(null);
    this.userSubject.next(null);
  }

  /**
   * Iniciar el proceso de autenticación con Microsoft
   */
  connect(): void {
    // Obtener la URL del frontend actual
    const frontendUrl = window.location.origin;

    // Abrir popup para autenticación
    const popup = window.open(
      `${this.API_BASE_URL}/auth/microsoft/login?frontendUrl=${encodeURIComponent(frontendUrl)}`,
      'microsoft-auth',
      'width=500,height=600,scrollbars=yes,resizable=yes'
    );

    if (!popup) {
      console.error('❌ No se pudo abrir el popup de Microsoft. Verifica que los popups estén habilitados.');
      return;
    }

    // Escuchar mensajes del popup
    let messageReceived = false;
    const messageListener = (event: MessageEvent) => {
      // Verificar origen del mensaje
      // Permitir mensajes del mismo origen o de la URL del callback
      const allowedOrigins = [
        window.location.origin,
        'https://demoa.app.inprovider.cl',
        'http://localhost:4200'
      ];
      
      if (!allowedOrigins.some(origin => event.origin === origin || event.origin.includes(origin.replace('https://', '').replace('http://', '')))) {
        console.warn('⚠️ Mensaje de origen no permitido:', event.origin);
        return;
      }

      console.log('📨 Mensaje recibido del popup:', event.data);

      if (event.data.type === 'MICROSOFT_AUTH_SUCCESS') {
        console.log('✅ Autenticación Microsoft exitosa:', event.data);
        messageReceived = true;

        // Guardar tokens y usuario
        const tokens = event.data.tokens;
        const user = event.data.user;

        this.saveTokens(tokens);
        this.userSubject.next(user);

        // Cerrar popup
        if (popup && !popup.closed) {
          popup.close();
        }

        // Limpiar listener
        window.removeEventListener('message', messageListener);
        clearInterval(checkClosed);
        clearTimeout(timeoutId);

        console.log('✅ Conexión Microsoft completada');
      } else if (event.data.type === 'MICROSOFT_AUTH_ERROR') {
        console.error('❌ Error en autenticación Microsoft:', event.data.error);
        messageReceived = true;

        // Cerrar popup
        if (popup && !popup.closed) {
          popup.close();
        }

        // Limpiar listener
        window.removeEventListener('message', messageListener);
        clearInterval(checkClosed);
        clearTimeout(timeoutId);
      }
    };

    window.addEventListener('message', messageListener);

    // Verificar si el popup se cerró manualmente
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        clearTimeout(timeoutId);
        window.removeEventListener('message', messageListener);
        console.log('🔒 Popup de Microsoft cerrado manualmente');
        
        if (!messageReceived) {
          console.warn('⚠️ Popup cerrado sin recibir mensaje de éxito o error');
        }
      }
    }, 1000);

    // Timeout para cerrar el popup si no se recibe respuesta después de 5 minutos
    const timeoutId = setTimeout(() => {
      if (!messageReceived && popup && !popup.closed) {
        console.warn('⏰ Timeout: cerrando popup después de 5 minutos sin respuesta');
        popup.close();
        window.removeEventListener('message', messageListener);
        clearInterval(checkClosed);
      }
    }, 5 * 60 * 1000); // 5 minutos
  }

  /**
   * Manejar el callback de autenticación (llamado desde el componente)
   */
  handleCallback(): Observable<{ success: boolean; user?: MicrosoftUser; error?: string }> {
    return this.http.get<any>(`${this.API_BASE_URL}/auth/microsoft/status`).pipe(
      map(response => {
        if (response.authenticated && response.user && response.tokenInfo) {
          const tokens: MicrosoftTokens = {
            accessToken: response.tokenInfo.hasAccessToken ? 'stored' : '',
            refreshToken: response.tokenInfo.hasRefreshToken ? 'stored' : '',
            expiresOn: new Date(response.tokenInfo.expiresOn),
            scopes: response.tokenInfo.scopes || []
          };

          this.saveTokens(tokens);
          this.userSubject.next(response.user);

          return { success: true, user: response.user };
        } else {
          return { success: false, error: 'No authentication data received' };
        }
      }),
      catchError(error => {
        console.error('Microsoft callback error:', error);
        return of({ success: false, error: error.message || 'Authentication failed' });
      })
    );
  }

  /**
   * Verificar si el usuario está conectado
   */
  isConnected(): boolean {
    const tokens = this.tokensSubject.value;
    if (!tokens) return false;

    // Verificar si el token no ha expirado
    return tokens.expiresOn > new Date();
  }

  /**
   * Obtener información del usuario conectado
   */
  getConnectedUser(): MicrosoftUser | null {
    return this.userSubject.value;
  }

  /**
   * Actualizar información del usuario conectado (método público)
   */
  updateUser(user: MicrosoftUser | null): void {
    this.userSubject.next(user);
  }

  /**
   * Obtener eventos del calendario para un rango de fechas
   */
  getEvents(startDate: Date, endDate: Date): Observable<MicrosoftCalendarEvent[]> {
    if (!this.isConnected()) {
      return of([]);
    }

    const params = {
      startDateTime: startDate.toISOString(),
      endDateTime: endDate.toISOString()
    };

    return this.http.get<any>(`${this.API_BASE_URL}/api/microsoft/events`, { params }).pipe(
      map(response => response.events || []),
      catchError(error => {
        console.error('Error fetching Microsoft events:', error);
        return of([]);
      })
    );
  }

  /**
   * Refrescar tokens
   */
  refreshTokens(): Observable<boolean> {
    return this.http.post<any>(`${this.API_BASE_URL}/auth/microsoft/refresh`, {}).pipe(
      map(response => {
        if (response.tokenInfo) {
          const tokens: MicrosoftTokens = {
            accessToken: response.tokenInfo.hasAccessToken ? 'refreshed' : '',
            refreshToken: response.tokenInfo.hasRefreshToken ? 'refreshed' : '',
            expiresOn: new Date(response.tokenInfo.expiresOn),
            scopes: response.tokenInfo.scopes || []
          };

          this.saveTokens(tokens);
          return true;
        }
        return false;
      }),
      catchError(error => {
        console.error('Error refreshing Microsoft tokens:', error);
        this.clearTokens();
        return of(false);
      })
    );
  }

  /**
   * Desconectar de Microsoft Calendar
   */
  disconnect(): Observable<boolean> {
    return this.http.post<any>(`${this.API_BASE_URL}/auth/microsoft/logout`, {}).pipe(
      map(() => {
        this.clearTokens();
        return true;
      }),
      catchError(error => {
        console.error('Error disconnecting from Microsoft:', error);
        this.clearTokens();
        return of(true);
      })
    );
  }

  /**
   * Limpiar conexión (para cambio de usuario)
   */
  clearConnection(): void {
    this.clearTokens();
  }

  /**
   * Verificar si el token está próximo a expirar
   */
  isTokenExpiringSoon(): boolean {
    const tokens = this.tokensSubject.value;
    if (!tokens) return false;

    const now = new Date();
    const expirationTime = new Date(tokens.expiresOn);
    const timeUntilExpiration = expirationTime.getTime() - now.getTime();

    // Considerar que está próximo a expirar si quedan menos de 5 minutos
    return timeUntilExpiration < 5 * 60 * 1000;
  }

  /**
   * Obtener tiempo restante del token
   */
  getTokenTimeRemaining(): string {
    const tokens = this.tokensSubject.value;
    if (!tokens) return 'No token';

    const now = new Date();
    const expirationTime = new Date(tokens.expiresOn);
    const timeUntilExpiration = expirationTime.getTime() - now.getTime();

    if (timeUntilExpiration <= 0) return 'Expirado';

    const minutes = Math.floor(timeUntilExpiration / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    return `${minutes}m`;
  }
}
